import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/auth';
import { useI18n } from '../i18n';
import { usePushEnableMessage, usePushSubscription } from '../hooks/usePushSubscription';
import { BellIcon, XMarkIcon } from './shared/Icons';
import { record } from '../observability';
import { local } from '../lib/safeStorage';

// A one-time push opt-in under the header for the people the bout events
// are for: athletes and coaches. It shows when push is supported, the device
// does not deny it, the read of the device finds no subscription, and the
// banner is not snoozed. The banner waits for `checked` (#664, bug 66):
// before the read, `enabled` is false on every device, and a subscribed
// device showed the banner for one moment at each page load. "Not now"
// snoozes it for 30 days on this device (the site storage). When the device
// subscribes, the banner never shows again.
//
// Why: staging held 11 push subscriptions after a production restore, so
// only a handful of people could receive a bout notification at all. The
// toggle at the bottom of the bell dropdown is not where an athlete looks.

const SNOOZE_KEY = 'lmf_push_prompt_snoozed_until';
const SNOOZE_DAYS = 30;
const AUDIENCE = ['athlete', 'coach', 'head_coach'];

/** A blocked storage gives `null`, so the banner is not snoozed. */
function snoozed(): boolean {
  const until = Number(local.get(SNOOZE_KEY) ?? 0);
  return until > Date.now();
}

export default function PushPromptBanner() {
  const { user, hasRole } = useAuthStore();
  const { t } = useI18n();
  const push = usePushSubscription();
  const explainEnable = usePushEnableMessage();
  const [hidden, setHidden] = useState(() => snoozed());

  // Re-read the snooze when the user changes (a different person on a shared device).
  useEffect(() => { setHidden(snoozed()); }, [user?.id]);

  if (!user || !hasRole(...AUDIENCE)) return null;
  if (!push.supported || push.permission === 'denied' || !push.checked || push.enabled || hidden) return null;

  const later = () => {
    local.set(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000)); // Private mode: the banner hides for this page view only.
    record({ event: 'push_prompt_snoozed' });
    setHidden(true);
  };

  return (
    // #615 — the prompt is for the screen: a printed page does not show it.
    <div data-testid="push-prompt" className="bg-blue-50 border-b border-blue-100 no-print">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3 text-sm">
        <BellIcon className="w-5 h-5 text-blue-600 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-blue-900">{t.notifications.pushPrompt.title}</div>
          <div className="text-blue-800 text-xs">{t.notifications.pushPrompt.body}</div>
        </div>
        <button onClick={() => push.enableWithResult().then(r => { if (r.ok) setHidden(true); else explainEnable(r); })} disabled={push.loading}
          className="btn-primary btn-sm shrink-0">
          {push.loading ? '…' : t.notifications.pushPrompt.enable}
        </button>
        <button onClick={later} className="btn-ghost btn-sm shrink-0 hidden sm:inline-flex">{t.notifications.pushPrompt.later}</button>
        <button onClick={later} aria-label={t.notifications.pushPrompt.later} className="text-blue-400 hover:text-blue-700 sm:hidden p-1">
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
